// src/features/AIDashboard/AIDashboard.jsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Paper,
  Chip,
  Stack,
  Alert,
  LinearProgress,
  Button
} from '@mui/material';
import {
  Analytics,
  Notifications,
  TrendingUp,
  Psychology,
  ModelTraining
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';

// Імпортуємо всі наші AI-компоненти
import useMLPredictionsTF from '../Analytics/hooks/useMLPredictionsTF';
import useAIRecommendations from '../AIRecommendations/hooks/useAIRecommendations';
import useWhatIfAnalysis from '../WhatIfAnalysis/hooks/useWhatIfAnalysis';
import { useNotifications } from '../../context/NotificationContext';

import RecommendationsDashboard from '../AIRecommendations/components/RecommendationsDashboard';
import WhatIfAnalysisDashboard from '../WhatIfAnalysis/components/WhatIfAnalysisDashboard';
import TensorFlowStatus from '../Analytics/components/TensorFlowStatus';
import NotificationPanel from '../../components/NotificationPanel/NotificationPanel';

const AIDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useI18n();

  // ВИПРАВЛЕНО: Використовуємо правильний хук
  const { notifyProductionTarget, unreadCount } = useNotifications();
  
  // Використовуємо всі AI-хуки
  const {
    predictions,
    predictionStats,
    modelState: mlState,
    modelInfo,
    refreshPredictions: refreshML
  } = useMLPredictionsTF({ useTensorFlow: true });

  const {
    recommendations,
    analysis: aiAnalysis,
    isLoading: aiLoading,
    applyRecommendation,
    criticalCount
  } = useAIRecommendations();

  const {
    scenarios,
    currentScenario,
    isLoading: whatIfLoading,
    runPredefinedScenario
  } = useWhatIfAnalysis();

  const tabs = [
    { label: t('ai_dashboard_overview'), icon: <Analytics /> },
    { label: t('ai_dashboard_recommendations'), icon: <Psychology /> },
    { label: t('ai_dashboard_whatif'), icon: <TrendingUp /> },
    { label: t('ai_dashboard_models'), icon: <ModelTraining /> }
  ];

  const handleApplyRecommendation = (recommendation) => {
    applyRecommendation(recommendation);
    // Можна додати сповіщення про застосування
    notifyProductionTarget({
      target: 'AI Recommendation',
      actual: 'Applied',
      achievement: 100
    });
  };

  if (mlState.isLoading && !mlState.isTrained) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('ai_dashboard_initializing')}
          </Typography>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('ai_dashboard_training_models')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Заголовок та статус */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {t('ai_dashboard_title')}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {t('ai_dashboard_subtitle')}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <TensorFlowStatus modelInfo={modelInfo} isLoading={mlState.isLoading} />
            
            <Chip
              icon={<Notifications />}
              label={`${unreadCount} ${t('notifications')}`}
              color={unreadCount > 0 ? "primary" : "default"}
              variant={unreadCount > 0 ? "filled" : "outlined"}
            />

            {criticalCount > 0 && (
              <Chip
                label={`${criticalCount} ${t('ai_critical_issues')}`}
                color="error"
                variant="filled"
              />
            )}
          </Stack>
        </Box>

        {/* Статусна панель */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  {t('ai_status_ml_model')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: mlState.isTrained ? 'success.main' : 'warning.main' }}>
                  {mlState.isTrained ? t('ai_status_trained') : t('ai_status_training')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('ai_accuracy')}: {modelInfo.accuracy.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  {t('ai_status_recommendations')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: recommendations.length > 0 ? 'info.main' : 'success.main' }}>
                  {recommendations.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {criticalCount > 0 ? `${criticalCount} ${t('ai_critical')}` : t('ai_all_optimal')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  {t('ai_status_predictions')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {predictions.length > 0 ? `${predictionStats.averageProduction}${t('labels_kg')}` : '--'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('ai_confidence')}: {predictionStats.confidence}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  {t('ai_status_scenarios')}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {scenarios.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('ai_completed')}: {scenarios.filter(s => s.status === 'completed').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Вкладки */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Вміст вкладок */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Прогнози виробництва */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('ai_production_forecast')}
                </Typography>
                
                {predictions.length > 0 ? (
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                      {predictionStats.averageProduction} {t('labels_kg')}/{t('field_day')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('ai_forecast_trend')}:{' '}
                      <Chip
                        label={predictionStats.trend === 'up' ? t('trend_up') : 
                               predictionStats.trend === 'down' ? t('trend_down') : t('trend_stable')}
                        color={predictionStats.trend === 'up' ? 'success' : 
                               predictionStats.trend === 'down' ? 'error' : 'default'}
                        size="small"
                      />
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      {predictions.slice(0, 5).map((pred, index) => (
                        <Box key={index} sx={{ textAlign: 'center', minWidth: 60 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {pred.predictedProduction}{t('labels_kg')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(pred.date).toLocaleDateString('uk-UA', { weekday: 'short' })}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('ai_no_predictions')}
                  </Typography>
                )}

                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={refreshML}
                  sx={{ mt: 2 }}
                  disabled={mlState.isLoading}
                >
                  {mlState.isLoading ? t('common_loading') : t('ai_refresh_forecast')}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Критичні рекомендації */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('ai_critical_recommendations')}
                </Typography>
                
                {criticalCount > 0 ? (
                  <Box>
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        {t('ai_critical_issues_found', { count: criticalCount })}
                      </Typography>
                    </Alert>

                    {recommendations
                      .filter(rec => rec.priority === 'critical' || rec.priority === 'high')
                      .slice(0, 3)
                      .map(rec => (
                        <Alert 
                          key={rec.id}
                          severity={rec.priority === 'critical' ? 'error' : 'warning'}
                          sx={{ mb: 1 }}
                          action={
                            <Button 
                              color="inherit" 
                              size="small"
                              onClick={() => handleApplyRecommendation(rec)}
                            >
                              {t('common_apply')}
                            </Button>
                          }
                        >
                          <Typography variant="body2">
                            {rec.title}
                          </Typography>
                        </Alert>
                      ))
                    }

                    <Button 
                      variant="contained" 
                      size="small"
                      onClick={() => setActiveTab(1)}
                      sx={{ mt: 1 }}
                    >
                      {t('ai_view_all_recommendations')}
                    </Button>
                  </Box>
                ) : (
                  <Alert severity="success">
                    <Typography variant="body2">
                      {t('ai_no_critical_issues')}
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Швидкі дії */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('ai_quick_actions')}
                </Typography>
                
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<TrendingUp />}
                    onClick={() => runPredefinedScenario('optimistic')}
                    disabled={whatIfLoading}
                  >
                    {t('what_if_quick_optimistic')}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<Psychology />}
                    onClick={() => setActiveTab(1)}
                  >
                    {t('ai_view_recommendations')}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<ModelTraining />}
                    onClick={refreshML}
                    disabled={mlState.isLoading}
                  >
                    {t('ai_retrain_model')}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <RecommendationsDashboard
          recommendations={recommendations}
          analysis={aiAnalysis}
          isLoading={aiLoading}
          onApplyRecommendation={handleApplyRecommendation}
        />
      )}

      {activeTab === 2 && (
        <WhatIfAnalysisDashboard />
      )}

      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('ai_model_status')}
                </Typography>
                
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('ai_model_type')}:</Typography>
                    <Chip 
                      label={modelInfo.type} 
                      color={modelInfo.usingTensorFlow ? "success" : "default"}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('ai_accuracy')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {modelInfo.accuracy.toFixed(1)}%
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('ai_training_data')}:</Typography>
                    <Typography variant="body2">
                      {modelInfo.trainingSize} {t('ai_records')}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('ai_data_quality')}:</Typography>
                    <Typography variant="body2">
                      {modelInfo.dataQuality}%
                    </Typography>
                  </Box>

                  {modelInfo.usingTensorFlow && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{t('ai_tf_backend')}:</Typography>
                      <Typography variant="body2">
                        {modelInfo.backend}
                      </Typography>
                    </Box>
                  )}
                </Stack>

                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={refreshML}
                  disabled={mlState.isLoading}
                  sx={{ mt: 2 }}
                >
                  {mlState.isLoading ? t('ai_training') : t('ai_retrain_model')}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('ai_system_health')}
                </Typography>
                
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>{t('ai_recommendations_health')}:</Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={recommendations.length > 0 ? Math.min(100, (criticalCount / recommendations.length) * 100) : 100}
                      color={criticalCount > 0 ? "error" : "success"}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>{t('ai_prediction_health')}:</Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={predictionStats.confidence}
                      color={predictionStats.confidence > 70 ? "success" : predictionStats.confidence > 50 ? "warning" : "error"}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>{t('ai_data_health')}:</Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={modelInfo.dataQuality}
                      color={modelInfo.dataQuality > 80 ? "success" : modelInfo.dataQuality > 60 ? "warning" : "error"}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Сповіщення */}
      <NotificationPanel />
    </Box>
  );
};

export default AIDashboard;